// NOTE: customers might require certain features of the app to be customized that others do not.
// In order to not scatter previous default values all over the code,
// we use this function to have a centralized set of default values that are being applied if no value is set in the customization.
// Note that keys are by default overridden, if a key should be merged additional code has to be added

// NOTE: settings can be overriden dynamically through the window object
import settings from "./settings";
import type { RegistrationField } from "@/components/user/userData";

export type LilliSettings = Partial<LilliDefaultSettings>;

declare global {
  interface Window {
    lilliSettings?: LilliSettings;
  }
}

export {};
export type LilliDefaultSettings = {
  title: string;
  termsAndConditionsLink: string;
  privacyPolicyLink: string;
  imprintLink: string | null;
  headerlogo: string;
  loginImage: string;
  showDetailsForQuiz: boolean;
  showDetailsForSurvey: boolean;
  showSeal: boolean;
  showCovidView: boolean;
  showCovidCertificatesToAdmin: boolean;
  showTestCenterView: boolean;
  showHardwareRestrictions: boolean;
  additionalCertificateTextEnabled: boolean;
  publicSharedAccount: number | null;
  coursesHideStar: boolean;
  courseListDescription: string | null;
  registrationRequiresKey: boolean;
  registrationKeyMinimumLength: number;
  registrationLinkOnLoginKey: string | undefined;
  hideContentManager: boolean;
  defaultSettings: {
    event: {
      selfRegistrationAllUsersInAccount: boolean;
      selfRegistration: boolean;
    };
    publication: { mandatory: boolean; generateCertificate: boolean };
  };
  hideEmailInUserAdmin: boolean;
  hideUsernameInUserAdmin: boolean;
  coursesUseImageSelector: boolean;
  showFreeCoursesAsFree: boolean;
  registrationRequiredDetails: Partial<
    Record<
      | "email"
      | "username"
      | "firstname"
      | "lastname"
      | "phone"
      | "address"
      | "birthday"
      | "birthyear"
      | "company"
      | "emergencyContact"
      | "title",
      { request: boolean; mandatory: boolean; step?: number }
    >
  >;
  registrationSteps: { label: string }[];
  hasRegistrationSteps: boolean;
  showRegistrationSideText: boolean;
  showRequiredFieldAnnotationInRegistration: boolean;
  reportsVisibility: Partial<
    Record<
      | "coursesUserCompleted"
      | "coursesUserAssignments"
      | "usersStates"
      | "usersRoleAssignments"
      | "accountStatistics"
      | "usersCovid"
      | "usersPracticalTests"
      | "usersTheoreticalCourses"
      | "testReport"
      | "userOrdersAggregate"
      | "ordersUserList"
      | "courseSessionsAggregate"
      | "userSessionsList"
      | "userCompletionsList",
      { visible: boolean; order?: number }
    >
  >;
  hidePrivacyPolicy: boolean;
  showMarketingConsent: boolean;
  showEventCategoryOnCard: boolean;
  userStateShowDownload: boolean;
  userStateTagOrder: string[];
  passwordRegex: string | undefined;
  customRegistrationFields: { fields: RegistrationField[] };
  customEventDateFields: {
    customizationId: string;
    fields: unknown[];
  };
  eventsHideFreeSeats: boolean;
  additionalPropertyHelpLink: string | null;
  publicationVatOptions: string[];
  publicationStandardVat: string;
  customPublicRegistrations: Record<string, unknown>;
  qGateFields: {
    id: string;
    type: string;
    label: string;
    mandatory: boolean;
    settings?: Record<string, string>;
  }[];
  customCertificateImage: string | null;
  courseAdminListFields: string[];
  flexLessonCompletedImage: string;
  flexCourseCompletedImage: string;
  courseProviderShareEnabled: boolean;
  emailAsUsername: boolean;
  supportedLanguages: string[];
  defaultLanguage: string;
  // Default polling interval, this is mostly used for testing
  pollingInterval: number;
  languageOverrides: Record<string, unknown>;
  /** Tag type names whose assigned values the logged-in user may change in their own profile. */
  userEditableTagTypes: string[];
};

export function getSetting<K extends keyof LilliDefaultSettings>(
  name: K,
): LilliDefaultSettings[K] {
  const defaults: LilliDefaultSettings = {
    title: "lilli e-learning",

    termsAndConditionsLink: "https://www.incontext.technology/impressum.html",
    privacyPolicyLink: "https://www.incontext.technology/datenschutz.html",
    imprintLink: null,
    headerlogo: "/img/logo.svg",
    loginImage: "/img/login-image.svg",

    showDetailsForQuiz: false,
    showDetailsForSurvey: false,
    showSeal: false,
    showCovidView: false,
    showCovidCertificatesToAdmin: false,
    showTestCenterView: false,
    showHardwareRestrictions: false,

    additionalCertificateTextEnabled: false,

    publicSharedAccount: null,

    coursesHideStar: false,

    courseListDescription: null,

    registrationRequiresKey: true,
    registrationKeyMinimumLength: 0,
    registrationLinkOnLoginKey: undefined,

    hideContentManager: false,
    pollingInterval: 5000,

    // Default settings for customers used in various places as defaults, using the according prefix.
    defaultSettings: {
      event: {
        selfRegistration: true,
        selfRegistrationAllUsersInAccount: true,
      },
      publication: {
        mandatory: false,
        generateCertificate: false,
      },
    },

    // User admin
    hideEmailInUserAdmin: false,
    hideUsernameInUserAdmin: false,

    // Image selector means that only admins can upload images,
    // While other users can select from existing images
    // this is especially useful for companies that want to control the images used in courses
    coursesUseImageSelector: false,

    showFreeCoursesAsFree: false, // if true, free courses are marked as "free" instead of not showing any price information

    registrationRequiredDetails: {
      email: { request: true, mandatory: true },
      username: { request: true, mandatory: true },
      firstname: { request: false, mandatory: false },
      lastname: { request: false, mandatory: false },
      phone: { request: false, mandatory: false },
      address: { request: false, mandatory: false },
      birthday: { request: false, mandatory: false },
      birthyear: { request: false, mandatory: false },
      company: { request: false, mandatory: false },
      emergencyContact: { request: false, mandatory: false },
    },
    registrationSteps: [],
    hasRegistrationSteps: false,
    showRegistrationSideText: false,
    showRequiredFieldAnnotationInRegistration: false,

    // Control visibility and display order of reports
    // Each report can be configured with:
    // - visible: boolean (default: true if not specified)
    // - order: number (default: 999 if not specified, lower numbers appear first)
    reportsVisibility: {
      coursesUserCompleted: { visible: true, order: 1 },
      coursesUserAssignments: { visible: true, order: 2 },
      usersStates: { visible: true, order: 3 },
      usersRoleAssignments: { visible: true, order: 4 },
      accountStatistics: { visible: true, order: 5 },
      usersCovid: { visible: true, order: 6 },
      usersPracticalTests: { visible: true, order: 7 },
      usersTheoreticalCourses: { visible: true, order: 8 },
      testReport: { visible: true, order: 9 },
      userOrdersAggregate: { visible: true, order: 10 },
      ordersUserList: { visible: true, order: 11 },
      courseSessionsAggregate: { visible: true, order: 12 },
    },

    // The privacy policy might not be needed if it is part of the terms and conditions
    hidePrivacyPolicy: false,

    showMarketingConsent: false,
    showEventCategoryOnCard: false,
    userStateShowDownload: false,
    userStateTagOrder: [],
    passwordRegex: undefined,

    customRegistrationFields: { fields: [] },

    customEventDateFields: { customizationId: "lilli", fields: [] },

    eventsHideFreeSeats: false,

    additionalPropertyHelpLink: null,

    publicationVatOptions: ["19", "7", "0"],
    publicationStandardVat: "19",
    // override registration setting for specific public registration keys
    customPublicRegistrations: {},

    qGateFields: [
      { id: "birthdate", type: "date", label: "birthday", mandatory: true },
      {
        id: "birthplace",
        type: "string",
        label: "birthplace",
        mandatory: true,
      },
      // NOTE: nationalityWithNonEuVisa also adds the additional, optional fields socialSecurity and workPermit (if the user is not from the EU)
      {
        id: "nationality",
        type: "nationalityWithNonEuVisa",
        label: "nationality",
        mandatory: true,
      },
      { id: "address", type: "string", label: "address", mandatory: true },
      { id: "city", type: "string", label: "city", mandatory: true },
      { id: "zipCode", type: "string", label: "zipCode", mandatory: true },
      { id: "country", type: "country", label: "country", mandatory: true },
      {
        id: "car",
        type: "switchString",
        label: "car",
        mandatory: false,
        settings: { subLabel: "carPlate", subId: "carPlate" },
      },
    ],

    customCertificateImage: null,

    /**
     * Course admin settings
     */

    // Some of the admin course fields are configurable see the component for details
    courseAdminListFields: ["language"],

    flexLessonCompletedImage: "/img/lilli_lektion_abgeschlossen.svg",
    flexCourseCompletedImage: "/img/lilli_kurs_abgeschlossen.svg",

    // Whether to show the provider share tab in course edit view
    courseProviderShareEnabled: false,

    emailAsUsername: true,
    supportedLanguages: [
      "de",
      "en",
      "bg",
      "cs",
      "el",
      "hr",
      "hu",
      "it",
      "lv",
      "pl",
      "pt",
      "ru",
      "ro",
      "es",
      "tr",
      "sr",
      "et",
      "nl",
      "ar",
    ],
    defaultLanguage: "de",

    languageOverrides: {},

    userEditableTagTypes: [],
  };

  const mergedSettings = { ...defaults, ...settings, ...window.lilliSettings };
  mergedSettings.registrationRequiredDetails = {
    ...defaults.registrationRequiredDetails,
    ...settings.registrationRequiredDetails,
    ...window.lilliSettings?.registrationRequiredDetails,
  };

  if (mergedSettings[name] === undefined) {
    console.warn(`Request for unknown setting: ${name}`);
  }

  return mergedSettings[name];
}
